import React, { useState } from 'react';
import { 
  Phone, 
  Mail, 
  MessageCircle, 
  Clock, 
  MapPin, 
  Send,
  CheckCircle,
  AlertCircle,
  HelpCircle,
  Users,
  Headphones,
  Shield
} from 'lucide-react';

interface ContactForm {
  name: string;
  email: string;
  subject: string;
  category: string;
  message: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
}

export const Contact: React.FC = () => {
  const [form, setForm] = useState<ContactForm>({
    name: '',
    email: '',
    subject: '',
    category: 'general',
    message: '',
    priority: 'medium'
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Simulate form submission
    setIsSubmitted(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img 
                src="/zippy-logo-simple.svg" 
                alt="ZippyHealth Logo"
                className="w-8 h-8"
              />
              <div>
                <h1 className="text-xl font-bold text-slate-800">ZippyHealth.io</h1>
                <p className="text-xs text-slate-500">Contact & Support</p>
              </div>
            </div>
            <button 
              onClick={() => window.history.back()}
              className="text-slate-600 hover:text-slate-800 font-medium"
            >
              ← Back
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {/* Header */}
        <div className="text-center mb-16">
          <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Headphones className="h-8 w-8 text-blue-600" />
          </div>
          <h1 className="text-4xl font-bold text-slate-900 mb-4">Contact Our Team</h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            We're here to help with your health tracking journey. Our support team is available 24/7 
            to assist with questions, technical issues, or feedback.
          </p>
        </div>

        {/* Contact Methods */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          
          {/* Emergency Support */}
          <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-2xl p-8 border border-red-200">
            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mb-6">
              <AlertCircle className="h-6 w-6 text-red-600" />
            </div>
            <h3 className="text-xl font-bold text-red-800 mb-3">Emergency Support</h3>
            <p className="text-red-700 mb-4">For urgent technical issues or security concerns</p>
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Phone className="h-4 w-4 text-red-600" />
                <span className="text-red-800 font-medium">1-800-ZIPPY-911</span>
              </div>
              <div className="flex items-center space-x-2">
                <Mail className="h-4 w-4 text-red-600" />
                <span className="text-red-800">emergency@zippyhealth.io</span>
              </div>
              <div className="flex items-center space-x-2">
                <Clock className="h-4 w-4 text-red-600" />
                <span className="text-red-700">24/7 Response</span>
              </div>
            </div>
          </div>

          {/* General Support */}
          <div className="bg-white rounded-2xl p-8 border border-slate-200 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
              <MessageCircle className="h-6 w-6 text-blue-600" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-3">General Support</h3>
            <p className="text-slate-600 mb-4">Questions about features, billing, or how to use ZippyHealth</p>
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Phone className="h-4 w-4 text-blue-600" />
                <span className="text-slate-700">1-800-ZIPPY-HELP</span>
              </div>
              <div className="flex items-center space-x-2">
                <Mail className="h-4 w-4 text-blue-600" />
                <span className="text-slate-700">support@zippyhealth.io</span>
              </div>
              <div className="flex items-center space-x-2">
                <Clock className="h-4 w-4 text-blue-600" />
                <span className="text-slate-600">Mon-Fri 8AM-8PM EST</span>
              </div>
            </div>
          </div>

          {/* Sales & Enterprise */}
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-8 border border-green-200">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-6">
              <Users className="h-6 w-6 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-green-800 mb-3">Sales & Enterprise</h3>
            <p className="text-green-700 mb-4">Custom solutions for healthcare providers and organizations</p>
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Phone className="h-4 w-4 text-green-600" />
                <span className="text-green-800">1-800-ZIPPY-SALES</span>
              </div>
              <div className="flex items-center space-x-2">
                <Mail className="h-4 w-4 text-green-600" />
                <span className="text-green-800">sales@zippyhealth.io</span>
              </div>
              <div className="flex items-center space-x-2">
                <Clock className="h-4 w-4 text-green-600" />
                <span className="text-green-700">Mon-Fri 9AM-6PM EST</span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          
          {/* Contact Form */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Send us a Message</h2>
            
            {isSubmitted ? (
              <div className="text-center py-8">
                <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-slate-900 mb-2">Message Sent!</h3>
                <p className="text-slate-600 mb-4">
                  Thank you for contacting us. We'll get back to you within 24 hours.
                </p>
                <button 
                  onClick={() => setIsSubmitted(false)}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="your.email@example.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Category
                    </label>
                    <select
                      name="category"
                      value={form.category}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="general">General Question</option>
                      <option value="technical">Technical Issue</option>
                      <option value="billing">Billing & Account</option>
                      <option value="feature">Feature Request</option>
                      <option value="privacy">Privacy & Security</option>
                      <option value="enterprise">Enterprise Sales</option>
                      <option value="provider">Healthcare Provider</option>
                      <option value="bug">Bug Report</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Priority
                    </label>
                    <select
                      name="priority"
                      value={form.priority}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Subject *
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={form.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Brief description of your inquiry"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Please provide details about your question or issue..."
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2"
                >
                  <Send className="h-5 w-5" />
                  <span>Send Message</span>
                </button>
              </form>
            )}
          </div>

          {/* Additional Information */}
          <div className="space-y-8">
            
            {/* FAQ */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center space-x-2">
                <HelpCircle className="h-6 w-6 text-blue-600" />
                <span>Quick Answers</span>
              </h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-slate-800 mb-1">How secure is my health data?</h4>
                  <p className="text-sm text-slate-600">We use AES-256 encryption and are pursuing HIPAA compliance. Your data is never sold to third parties.</p>
                </div>
                <div>
                  <h4 className="font-semibold text-slate-800 mb-1">Can I export my data?</h4>
                  <p className="text-sm text-slate-600">Yes! You can export all your data anytime from the Settings page. We support multiple formats.</p>
                </div>
                <div>
                  <h4 className="font-semibold text-slate-800 mb-1">Do you offer healthcare provider accounts?</h4>
                  <p className="text-sm text-slate-600">Yes, we have specialized dashboards for healthcare providers with patient collaboration tools.</p>
                </div>
                <div>
                  <h4 className="font-semibold text-slate-800 mb-1">What devices do you support?</h4>
                  <p className="text-sm text-slate-600">We integrate with 10+ popular health devices including fitness trackers, glucose monitors, and smart scales.</p>
                </div>
              </div>
            </div>

            {/* Office Information */}
            <div className="bg-gradient-to-br from-slate-100 to-slate-200 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center space-x-2">
                <MapPin className="h-6 w-6 text-slate-600" />
                <span>Our Office</span>
              </h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-slate-800">ZippyHealth.io Headquarters</h4>
                  <p className="text-slate-600">
                    123 Health Technology Blvd<br />
                    Innovation City, IC 12345<br />
                    United States
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-slate-800">Business Hours</h4>
                  <p className="text-slate-600">
                    Monday - Friday: 8:00 AM - 8:00 PM EST<br />
                    Saturday: 10:00 AM - 4:00 PM EST<br />
                    Sunday: Emergency support only
                  </p>
                </div>
              </div>
            </div>

            {/* Security Notice */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 border border-blue-200">
              <div className="flex items-start space-x-3">
                <Shield className="h-6 w-6 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-blue-800 mb-2">Security Notice</h3>
                  <p className="text-blue-700 text-sm">
                    For security reasons, we will never ask for your password or sensitive health information 
                    over email or phone. If you receive suspicious communications claiming to be from 
                    ZippyHealth.io, please report them to security@zippyhealth.io immediately.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact; 
